import type { Ref } from 'vue'
import type { Content } from '../../types/content'
import type { TableKey } from '../../types/database'
import type { SortOrder } from '../../types/sorting'
import { useDatabase } from './useDatabase'
import { useContextsStore } from '../stores/contexts'
import { liveQuery } from 'dexie'
import { tryCatch } from '../utils/result'

/** Minimal shape required by the composable. All entity types satisfy this. */
interface EntityBase {
  id: string
  createdAt: number
  updatedAt: number
  deletedAt: number | null
  syncedAt: number | null
}

/**
 * Configuration options for the generic entity store composable.
 */
export interface EntityStoreOptions<T extends EntityBase> {
  /** Dexie table name (e.g. 'notes', 'lists', 'tasks', 'references') */
  table: string
  /** Prefix for persisting sort preferences (e.g. 'notes' → 'notes_sort_by') */
  sortPreferencePrefix?: string
  /** Default sort field. Defaults to 'modification' */
  defaultSortBy?: string
  /** Default sort direction. Defaults to 'desc' */
  defaultSortOrder?: SortOrder
  /** Additional sort strategies merged with the base ones (modification, creation) */
  sortStrategies?: Record<string, (a: T, b: T) => number>
  /** Hook called before persisting an update. Use to transform the payload (e.g. compute preview) */
  beforeUpdate?: (
    updates: Partial<T & { id: string }>
  ) => Partial<T & { id: string }>
}

/**
 * Generic composable that provides reactive Dexie subscription, CRUD operations,
 * sorting, favourites and context-move logic for any entity type.
 * @param options - Configuration for the entity store
 * @returns Reactive state, computed properties and CRUD functions
 */
export function useEntityStore<T extends EntityBase>(
  options: EntityStoreOptions<T>
) {
  const dbOps = useDatabase()
  const contextsStore = useContextsStore()
  const tableKey = options.table as TableKey

  // --- Reactive state ---
  const items = ref<T[]>([]) as Ref<T[]>
  const sortBy = ref<string>(options.defaultSortBy ?? 'modification')
  const sortOrder = ref<SortOrder>(
    (options.defaultSortOrder as SortOrder) ?? 'desc'
  )

  // --- Dexie live subscription ---
  let subscription: { unsubscribe: () => void } | null = null

  /**
   * Subscribes to live query updates for items in the given context.
   * @param contextId - The context ID to filter items by
   */
  function subscribe(contextId: string): void {
    if (subscription) {
      subscription.unsubscribe()
    }

    const observable = liveQuery(() =>
      dbOps.getItemsByContext<Content>(tableKey, contextId)
    )

    subscription = observable.subscribe((newItems) => {
      items.value = newItems as unknown as T[]
    })
  }

  watch(
    () => contextsStore.currentContext?.id,
    (contextId) => {
      if (contextId !== undefined) {
        subscribe(contextId)
      }
    },
    { immediate: true }
  )

  // --- Sort strategies ---
  const baseSortStrategies: Record<string, (a: T, b: T) => number> = {
    modification: (a: T, b: T) => a.updatedAt - b.updatedAt,
    creation: (a: T, b: T) => a.createdAt - b.createdAt
  }

  const strategies: Record<string, (a: T, b: T) => number> = {
    ...baseSortStrategies,
    ...(options.sortStrategies ?? {})
  }

  /**
   * Sorts an array of items using the current sort strategy and order.
   * @param itemsToSort - The array to sort
   * @returns A new sorted array
   */
  function sortItems(itemsToSort: T[]): T[] {
    const strategy = strategies[sortBy.value]
    if (!strategy) return [...itemsToSort]
    const sorted = [...itemsToSort].sort(strategy)
    return sortOrder.value === 'desc' ? sorted.reverse() : sorted
  }

  /** All items sorted by the current strategy and order. */
  const sortedItems = computed(() => sortItems(items.value))

  // --- CRUD ---

  /**
   * Retrieves an item by its ID from the database.
   * @param id - The item ID
   * @returns The item or null if not found
   */
  async function getById(id: string): Promise<T | null> {
    const item = await dbOps.getItemById<Content>(tableKey, id)
    return (item as unknown as T) ?? null
  }

  /**
   * Adds a new item to the database within the current context.
   * @param fields - Partial fields for the new item
   * @returns The created item or null on failure
   */
  async function add(fields: Partial<T>): Promise<T | null> {
    if (!contextsStore.currentContext) return null

    const newItem = {
      ...fields,
      contextId: contextsStore.currentContext.id
    }

    const result = await tryCatch(() =>
      dbOps.addItem(
        tableKey,
        newItem as unknown as Omit<
          Content,
          'id' | 'createdAt' | 'updatedAt' | 'deletedAt' | 'syncedAt'
        >
      )
    )
    if (!result.ok) {
      console.error(`Failed to add ${options.table}:`, result.error)
      return null
    }
    return result.value as unknown as T
  }

  /**
   * Updates an existing item in the database with optimistic local state update.
   * @param updates - Partial fields to update, must include id
   */
  async function update(updates: Partial<T & { id: string }>): Promise<void> {
    if (!updates.id) return
    const id = updates.id

    const safeUpdates = options.beforeUpdate
      ? options.beforeUpdate(updates)
      : updates

    const result = await tryCatch(() =>
      dbOps.updateItem(
        tableKey,
        safeUpdates as Partial<Content & { id: string }>
      )
    )
    if (!result.ok) {
      console.error(`Failed to update ${options.table}:`, result.error)
      return
    }

    const index = items.value.findIndex((item) => item.id === id)
    if (index !== -1 && items.value[index] !== undefined) {
      items.value[index] = { ...items.value[index], ...safeUpdates }
    }
    if (updates.deletedAt === null) {
      items.value.push(result.value as unknown as T)
    }
  }

  /**
   * Soft-deletes an item by setting deletedAt to the current timestamp.
   * @param id - The item ID to remove
   */
  async function remove(id: string): Promise<void> {
    await update({ id, deletedAt: Date.now() } as Partial<T & { id: string }>)
  }

  /**
   * Soft-deletes multiple items.
   * @param ids - Array of item IDs to remove
   */
  async function removeBulk(ids: string[]): Promise<void> {
    for (const id of ids) {
      await remove(id)
    }
  }

  /**
   * Restores a soft-deleted item by setting deletedAt to null.
   * @param id - The item ID to restore
   */
  async function restore(id: string): Promise<void> {
    await update({ id, deletedAt: null } as Partial<T & { id: string }>)
  }

  /**
   * Restores multiple soft-deleted items.
   * @param ids - Array of item IDs to restore
   */
  async function restoreBulk(ids: string[]): Promise<void> {
    for (const id of ids) {
      await restore(id)
    }
  }

  /**
   * Moves items to a different context.
   * @param ids - Array of item IDs to move
   * @param contextId - Target context ID
   */
  async function moveToContext(
    ids: string[],
    contextId: string
  ): Promise<void> {
    for (const id of ids) {
      await update({ id, contextId } as unknown as Partial<T & { id: string }>)
    }
  }

  // --- Favourites ---

  /**
   * Toggles the favourite status of a single item.
   * @param id - The item ID
   */
  async function toggleFavourite(id: string): Promise<void> {
    const item = items.value.find((i) => i.id === id)
    if (!item) return
    const current = (item as T & { favourite?: boolean }).favourite
    await update({ id, favourite: !current } as unknown as Partial<
      T & { id: string }
    >)
  }

  /**
   * Toggles favourite for multiple items. If all are favourites, unfavourites them; otherwise favourites all.
   * @param ids - Array of item IDs
   */
  async function toggleFavouriteBulk(ids: string[]): Promise<void> {
    const selected = items.value.filter((i) => ids.includes(i.id))
    const allFavourite = selected.every(
      (i) => (i as T & { favourite?: boolean }).favourite
    )
    const newValue = !allFavourite
    for (const id of ids) {
      await update({ id, favourite: newValue } as unknown as Partial<
        T & { id: string }
      >)
    }
  }

  // --- Sort preferences ---

  /**
   * Sets the sort-by option and persists it.
   * @param option - The sort field name
   */
  async function setSortBy(option: string): Promise<void> {
    sortBy.value = option
    if (options.sortPreferencePrefix) {
      await dbOps.setPreference(
        `${options.sortPreferencePrefix}_sort_by`,
        option
      )
    }
  }

  /**
   * Toggles sort order between 'asc' and 'desc' and persists it.
   */
  async function toggleSortOrder(): Promise<void> {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
    if (options.sortPreferencePrefix) {
      await dbOps.setPreference(
        `${options.sortPreferencePrefix}_sort_order`,
        sortOrder.value
      )
    }
  }

  /**
   * Loads persisted sort preferences from the database.
   */
  async function loadSortPreferences(): Promise<void> {
    if (!options.sortPreferencePrefix) return
    sortBy.value =
      (await dbOps.getPreference(`${options.sortPreferencePrefix}_sort_by`)) ??
      options.defaultSortBy ??
      'modification'
    sortOrder.value =
      ((await dbOps.getPreference(
        `${options.sortPreferencePrefix}_sort_order`
      )) as SortOrder) ??
      options.defaultSortOrder ??
      'desc'
  }

  return {
    items,
    sortBy,
    sortOrder,
    sortedItems,
    sortItems,
    getById,
    add,
    update,
    remove,
    removeBulk,
    restore,
    restoreBulk,
    moveToContext,
    toggleFavourite,
    toggleFavouriteBulk,
    setSortBy,
    toggleSortOrder,
    loadSortPreferences
  }
}
